import { ENVS } from "config/envs";
import { hasAdsConsent } from "./consent";

const OPENAI_ADS_SDK_URL = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const OPENAI_ADS_SCRIPT_ID = "rivetai-openai-ads-pixel";

export type OpenAiAdsMeasureEventName = "order_created" | "registration_completed";

export type OpenAiAdsMeasureData = {
  type: "contents" | "customer_action";
  amount?: number;
  currency?: string;
};

export type OpenAiAdsMeasureOptions = {
  event_id?: string;
};

type OaiqQueue = ((...args: unknown[]) => void) & {
  q: unknown[][];
};

declare global {
  interface Window {
    oaiq?: OaiqQueue;
  }
}

let pixelInitialized = false;

const isOpenAiAdsDebugEnabled = (): boolean => process.env.NODE_ENV !== "production";

export const isOpenAiAdsPixelConfigured = (): boolean => Boolean(ENVS.openaiAdsPixelId?.trim());

const ensureOaiqQueue = (): OaiqQueue | null => {
  if (typeof window === "undefined") {
    return null;
  }

  if (window.oaiq) {
    return window.oaiq;
  }

  const queue: OaiqQueue = Object.assign(
    (...args: unknown[]) => {
      queue.q.push(args);
    },
    { q: [] as unknown[][] }
  );

  window.oaiq = queue;
  return queue;
};

const injectOpenAiAdsSdk = (): void => {
  if (typeof document === "undefined") {
    return;
  }
  if (document.getElementById(OPENAI_ADS_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");
  script.id = OPENAI_ADS_SCRIPT_ID;
  script.async = true;
  script.src = OPENAI_ADS_SDK_URL;
  document.head.appendChild(script);
};

/**
 * Load and initialize the OpenAI Ads Measurement Pixel when a pixel ID is configured.
 * Idempotent. Call only after analytics consent.
 */
export const ensureOpenAiAdsPixel = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  const pixelId = ENVS.openaiAdsPixelId?.trim();
  if (!pixelId) {
    return;
  }

  const oaiq = ensureOaiqQueue();
  if (!oaiq) {
    return;
  }

  injectOpenAiAdsSdk();

  if (pixelInitialized) {
    return;
  }

  oaiq("init", {
    pixelId,
    debug: isOpenAiAdsDebugEnabled(),
  });
  pixelInitialized = true;
};

export const measureOpenAiAdsEvent = (
  eventName: OpenAiAdsMeasureEventName,
  data: OpenAiAdsMeasureData,
  options?: OpenAiAdsMeasureOptions
): void => {
  if (typeof window === "undefined") {
    return;
  }
  if (!isOpenAiAdsPixelConfigured()) {
    return;
  }
  // With regional consent on, ad measurement needs ads consent (denied under GPC).
  if (ENVS.regionalConsent && !hasAdsConsent()) {
    return;
  }

  ensureOpenAiAdsPixel();
  const oaiq = window.oaiq;
  if (!oaiq) {
    return;
  }

  if (options?.event_id) {
    oaiq("measure", eventName, data, { event_id: options.event_id });
    return;
  }

  oaiq("measure", eventName, data);
};

export const measureOpenAiAdsRegistrationCompleted = (): void => {
  measureOpenAiAdsEvent("registration_completed", { type: "customer_action" });
};

export const measureOpenAiAdsOrderCreated = (input?: {
  amountUsd?: number;
  eventId?: string;
}): void => {
  const data: OpenAiAdsMeasureData = { type: "contents" };
  if (typeof input?.amountUsd === "number" && Number.isFinite(input.amountUsd)) {
    data.amount = Math.round(input.amountUsd * 100);
    data.currency = "USD";
  }

  measureOpenAiAdsEvent(
    "order_created",
    data,
    input?.eventId ? { event_id: input.eventId } : undefined
  );
};

/** Test helper — resets module init state between Vitest cases. */
export const resetOpenAiAdsPixelForTests = (): void => {
  pixelInitialized = false;
  if (typeof window !== "undefined") {
    delete window.oaiq;
  }
  if (typeof document !== "undefined") {
    document.getElementById(OPENAI_ADS_SCRIPT_ID)?.remove();
  }
};
